import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { SwiperModule } from 'swiper/angular';
import { SwiperOptions } from 'swiper';
import { CsIconModule } from '@components/icon';
import { ButtonModule } from '@components/button';
import {ButtonCtaComponent} from '@components/button-cta/button-cta.component';

interface ExtraImageWithTag {
  title?: string;
  content?: string;
  imageLink: string;
  tag: string;
  priority: number;
}

interface SliderProduct {
  product: {
    objectId: string;
    title?: string;
    hiddenTitle?: string;
    rebrandedImageWithBackground?: string;
    quantity?: string;
    quantityUnit?: string;
    extraImagesWithTags: ExtraImageWithTag[];
  };
  productName?: string;
}

interface SliderRegimen {
  class?: string;
  regimenId?: string;
}

interface SlideView {
  objectId: string;
  product: SliderProduct;
  imageLink: string;
  isTagged: boolean;
  title: string;
  subtitle?: string;
  quantityLabel: string;
}

@Component({
  selector: 'app-regimen-products-slider',
  templateUrl: './regimen-products-slider.html',
  imports: [SwiperModule, CsIconModule, ButtonModule, ButtonCtaComponent],
})
export class RegimenProductsSliderComponent {
  private static readonly REGIMEN_SLIDER_TAG = 'regimenSlider';

  regimen = input.required<SliderRegimen>();
  products = input.required<SliderProduct[]>();

  readonly slides = computed<SlideView[]>(() => this.products().map((product) => this.toSlideView(product)));

  private readonly router = inject(Router);

  readonly swiperConfig: SwiperOptions = {
    slidesPerView: 1.4,
    spaceBetween: 0,
    pagination: false,
    navigation: false,
    autoplay: false,
  };

  goToInstructions(): void {
    this.router.navigate(['/regimen/instructions'], {
      queryParams: this.routeQueryParams(),
    });
  }

  goToProductDetails(product: SliderProduct): void {
    this.router.navigate(['/user/regimen/products'], {
      queryParams: { ...this.routeQueryParams(), productId: product.product.objectId },
    });
  }

  private routeQueryParams(): { class: string; regimenId: string } {
    const regimen = this.regimen();
    return { class: regimen.class, regimenId: regimen.regimenId };
  }

  private toSlideView(product: SliderProduct): SlideView {
    const { product: details, productName } = product;
    const quantityLabel = [details.quantity, details.quantityUnit].filter(Boolean).join(' ');
    const sliderImage = this.pickRegimenSliderImage(details.extraImagesWithTags);

    if (sliderImage) {
      return {
        objectId: details.objectId,
        product,
        imageLink: sliderImage.imageLink,
        isTagged: true,
        title: sliderImage.title || '',
        quantityLabel,
      };
    }

    return {
      objectId: details.objectId,
      product,
      imageLink: details.rebrandedImageWithBackground,
      isTagged: false,
      title: details.title || productName || '',
      subtitle: details.hiddenTitle,
      quantityLabel,
    };
  }

  private pickRegimenSliderImage(extraImagesWithTags: ExtraImageWithTag[]): ExtraImageWithTag | undefined {
    if (!extraImagesWithTags?.length) {
      return undefined;
    }

    return extraImagesWithTags
      .filter((image) => image.tag === RegimenProductsSliderComponent.REGIMEN_SLIDER_TAG)
      .reduce<ExtraImageWithTag | undefined>(
        (highest, image) => (!highest || image.priority > highest.priority ? image : highest),
        undefined,
      );
  }
}
